import { useEffect, useState } from "react";
import { api, ApiError, loadSession } from "../lib/api.js";

type Member = {
  id: string;
  name: string;
  email: string;
  username?: string;
  role: string;
  status: string;
  mfaEnabled: boolean;
  lastLoginAt?: string;
};

type ListState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; members: Member[]; seatLimit: number; seatsUsed: number };

type CreateRole = "admin" | "analyst" | "billing" | "support";
type AssignableRole = "owner" | "admin" | "analyst" | "billing" | "support";

const ASSIGNABLE_ROLES: { value: AssignableRole; label: string; note: string }[] = [
  { value: "owner",   label: "Owner",    note: "Full workspace control, including billing and deletion" },
  { value: "admin",   label: "Admin",    note: "Manage members, DHIS2, forwarding, portals" },
  { value: "analyst", label: "Analyst",  note: "Explore datasets, run reports, view audit" },
  { value: "billing", label: "Billing",  note: "Invoices, payment methods, plan changes" },
  { value: "support", label: "Support",  note: "Read-only workspace health diagnostics" }
];

export default function Team() {
  const session = loadSession();
  const canManage = session?.role === "admin";
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [role, setRole] = useState<CreateRole>("analyst");
  const [password, setPassword] = useState("");

  async function refresh() {
    try {
      const res = await api.listWorkspaceMembers();
      setState({ status: "ready", members: res.members as Member[], seatLimit: res.seatLimit, seatsUsed: res.seatsUsed });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load members";
      setState({ status: "error", message });
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function onCreate(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await api.createWorkspaceMember({
        name: name.trim(),
        email: email.trim(),
        username: username.trim(),
        role,
        password
      });
      setName(""); setEmail(""); setUsername(""); setPassword("");
      await refresh();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed to add member");
    } finally {
      setSubmitting(false);
    }
  }

  async function onRemove(member: Member) {
    if (!confirm(`Remove ${member.name} from this workspace?`)) return;
    try {
      await api.removeWorkspaceMember(member.id);
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to remove member");
    }
  }

  async function onChangeRole(member: Member, nextRole: AssignableRole) {
    if (nextRole === member.role) return;
    if (!session?.workspaceId) { alert("No workspace context."); return; }
    if (!confirm(`Change ${member.name}'s role from ${member.role} to ${nextRole}?`)) return;
    try {
      await api.setWorkspaceMemberRole(session.workspaceId, member.id, nextRole);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed to change role");
    }
  }

  if (state.status === "loading") {
    return <div className="content"><p className="muted">Loading members…</p></div>;
  }
  if (state.status === "error") {
    return <div className="content"><div className="alert alert-error">{state.message}</div></div>;
  }

  const { members, seatLimit, seatsUsed } = state;
  const seatsRemaining = Math.max(0, seatLimit - seatsUsed);
  const atLimit = seatsRemaining === 0;

  return (
    <div className="content">
      <div className="page-head">
        <h1>Team</h1>
        <p className="muted">
          Manage workspace members. Seats used: <strong>{seatsUsed}</strong> / {seatLimit}
          {atLimit && <span className="pill pill-warning" style={{ marginLeft: 8 }}>seat limit reached</span>}
        </p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head"><strong>Members</strong></div>
        <div className="card-body">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border, #e5e7eb)" }}>
                <th style={{ padding: "8px 4px" }}>Name</th>
                <th style={{ padding: "8px 4px" }}>Username</th>
                <th style={{ padding: "8px 4px" }}>Email</th>
                <th style={{ padding: "8px 4px" }}>Role</th>
                <th style={{ padding: "8px 4px" }}>Status</th>
                <th style={{ padding: "8px 4px" }}></th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id} style={{ borderBottom: "1px solid var(--border-soft, #f1f5f9)" }}>
                  <td style={{ padding: "8px 4px" }}>{member.name}</td>
                  <td style={{ padding: "8px 4px" }}>{member.username ?? "—"}</td>
                  <td style={{ padding: "8px 4px" }}>{member.email}</td>
                  <td style={{ padding: "8px 4px" }}>
                    {canManage && member.id !== session?.userId ? (
                      <select
                        value={member.role}
                        onChange={(e) => onChangeRole(member, e.target.value as AssignableRole)}
                        title={ASSIGNABLE_ROLES.find((r) => r.value === member.role)?.note}
                      >
                        {ASSIGNABLE_ROLES.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    ) : member.role}
                  </td>
                  <td style={{ padding: "8px 4px" }}>{member.status}</td>
                  <td style={{ padding: "8px 4px", textAlign: "right" }}>
                    {canManage && member.id !== session?.userId && member.role !== "owner" && (
                      <button type="button" className="btn" onClick={() => onRemove(member)}>Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {canManage ? (
        <div className="card">
          <div className="card-head"><strong>Invite a member</strong></div>
          <div className="card-body">
            {atLimit ? (
              <div className="alert alert-warning">
                This workspace has reached its seat limit. Upgrade your plan to add more members.
              </div>
            ) : (
              <form onSubmit={onCreate}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div className="field">
                    <label htmlFor="m-name">Name</label>
                    <input id="m-name" type="text" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                  <div className="field">
                    <label htmlFor="m-email">Email</label>
                    <input id="m-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                  <div className="field">
                    <label htmlFor="m-username">Username</label>
                    <input id="m-username" type="text" required minLength={3} pattern="^[A-Za-z0-9._-]+$" value={username} onChange={(e) => setUsername(e.target.value)} />
                  </div>
                  <div className="field">
                    <label htmlFor="m-role">Role</label>
                    <select id="m-role" value={role} onChange={(e) => setRole(e.target.value as CreateRole)}>
                      <option value="analyst">Analyst</option>
                      <option value="admin">Admin</option>
                      <option value="billing">Billing</option>
                      <option value="support">Support</option>
                    </select>
                  </div>
                  <div className="field" style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="m-password">Initial password</label>
                    <input id="m-password" type="password" required minLength={8} maxLength={128} value={password} onChange={(e) => setPassword(e.target.value)} />
                    <small className="muted">Share this securely with the member. They should change it on first login.</small>
                  </div>
                </div>
                {formError && <div className="alert alert-error" style={{ marginTop: 12 }}>{formError}</div>}
                <div style={{ marginTop: 12 }}>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? "Adding…" : "Add member"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      ) : (
        <p className="muted">Only workspace admins can manage members.</p>
      )}
    </div>
  );
}
